'use client';

import { useMultiVideoPlayback } from '@app/_shared/hooks/useMultiVideoPlayback';
import { type JSX, useRef, useState } from 'react';

import { Button, Tooltip, cn, getAccessibleFocusStyles } from '@videoblocks/design-system';
import { AiWand } from '@videoblocks/design-system/icons';

import { DEFAULT_VIDEO_ASPECT_RATIO, VERTICAL_ASPECT_RATIO_THRESHOLD } from '../../_context/constants';

const EDIT_WITH_AI_LABEL = 'Edit with AI';

/** Mirrors Button's own disabled look, which is keyed to the `disabled` prop we can't use here. */
const ARIA_DISABLED_CLASSES =
    'bg-gray-200 text-gray-500 border-gray-200 hover:bg-gray-200 hover:text-gray-500 hover:border-gray-200 cursor-not-allowed';

export interface AiToolkitVideoCardProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Must be unique per page; playback coordination keys off it. */
    videoId: string;
    videoUrl: string;
    posterUrl: string;
    title: string;
    onEditWithAiClick: () => void;
    onSelect?: () => void;
    /** The item's own ratio, so object-cover never crops. Vertical items are < 1, e.g. 0.5625. */
    aspectRatio?: number;
    selected?: boolean;
    /** Locks the card while a generation runs: still previewable, but not selectable. */
    disabled?: boolean;
    disabledTooltip?: string;
    className?: string;
}

export function AiToolkitVideoCard({
    videoId,
    videoUrl,
    posterUrl,
    title,
    onEditWithAiClick,
    onSelect,
    aspectRatio = DEFAULT_VIDEO_ASPECT_RATIO,
    selected = false,
    disabled = false,
    disabledTooltip = 'Generation in progress',
    className,
    style,
    onMouseEnter,
    onMouseLeave,
    ...rest
}: AiToolkitVideoCardProps): JSX.Element {
    const videoRef = useRef<HTMLVideoElement>(null);
    const { play, pause, reset } = useMultiVideoPlayback(videoId, videoRef);
    const [isHovering, setIsHovering] = useState(false);

    // A labelled pill is too wide for a vertical card, so the label moves into the tooltip.
    const isVertical = aspectRatio <= VERTICAL_ASPECT_RATIO_THRESHOLD;
    const tooltipTitle = disabled ? disabledTooltip : isVertical ? EDIT_WITH_AI_LABEL : undefined;

    // The ring rides an overlay because an inset ring on the container paints behind the video.
    const ringColorClassName = selected
        ? disabled
            ? 'inset-ring-white'
            : 'inset-ring-yellow-500'
        : !disabled && isHovering && 'inset-ring-yellow-500';

    const cta = (
        <Button
            // Native disabled would emit no hover or focus events, so the tooltip could never open.
            aria-disabled={disabled || undefined}
            aria-label={isVertical ? EDIT_WITH_AI_LABEL : undefined}
            iconPrefix={
                // The SVG hardcodes fill="#fff", which beats Button's fill-current wrapper.
                isVertical ? <AiWand width={16} height={16} className="[&_path]:fill-current" /> : undefined
            }
            buttonClassName={cn(
                'rounded-[999px]',
                // gap-0 because Button still renders an empty label span next to the icon.
                isVertical && 'size-10 p-0 gap-0',
                disabled && ARIA_DISABLED_CLASSES,
            )}
            onClick={(e) => {
                e.stopPropagation();
                if (disabled) {
                    e.preventDefault();
                    return;
                }
                onEditWithAiClick();
            }}
        >
            {isVertical ? null : EDIT_WITH_AI_LABEL}
        </Button>
    );

    return (
        <div
            className={cn('relative size-full overflow-hidden rounded-sm', className)}
            style={{ aspectRatio, ...style }}
            onMouseEnter={(e) => {
                setIsHovering(true);
                void play();
                onMouseEnter?.(e);
            }}
            onMouseLeave={(e) => {
                setIsHovering(false);
                pause();
                reset();
                onMouseLeave?.(e);
            }}
            data-testid="ai-toolkit-video-card"
            {...rest}
        >
            <video
                ref={videoRef}
                src={videoUrl}
                poster={posterUrl}
                aria-label={title}
                className="size-full object-cover bg-black rounded-sm border border-white/20"
                preload="metadata"
                muted
                loop
                playsInline
                data-testid="ai-toolkit-video-card-video"
            />
            {ringColorClassName && (
                <div
                    className={cn('pointer-events-none absolute inset-0 rounded-sm inset-ring-2', ringColorClassName)}
                    data-testid="ai-toolkit-video-card-ring"
                />
            )}
            {onSelect && !disabled && (
                <button
                    type="button"
                    aria-label={`Select ${title}`}
                    className={cn('absolute inset-0 cursor-pointer', getAccessibleFocusStyles())}
                    onClick={() => onSelect()}
                    data-testid="ai-toolkit-video-card-select"
                />
            )}
            <div
                className={cn(
                    'absolute bottom-2 left-2 transition-opacity',
                    // focus-visible, not focus-within, so a click doesn't leave it stuck on screen.
                    !isHovering && 'opacity-0 has-[:focus-visible]:opacity-100',
                )}
                data-testid="ai-toolkit-video-card-cta"
            >
                {tooltipTitle ? <Tooltip title={tooltipTitle} variant="light" trigger={cta} /> : cta}
            </div>
        </div>
    );
}
